// Attribution marketing : capture et persistance des paramètres de campagne
// (UTM + identifiants de clic) pour attribuer les commandes aux campagnes payantes.
// Généralise le petit getUtmParams() qui n'existait que sur la page app-mobile.

const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
const FIRST_TOUCH = 'ogg_attr_first';
const LAST_TOUCH = 'ogg_attr_last';
const QUOTE_SID = 'ogg_quote_sid';       // legacy : cookie 90 j (comportement pré-Lot 1)
const DEVICE_ID = 'ogg_device_id';       // localStorage : identité stable du navigateur
const SESSION_SID = 'ogg_quote_sid';     // sessionStorage : une demande (Lot 1)
const PREFILL = 'ogg_quote_prefill';     // localStorage : dernières valeurs connues (préremplissage)
const MAX_AGE_DAYS = 90;

// Fonctionnalités devis (historisation, tél requis, vendeur, OTP) : toujours actives.
// (Anciens drapeaux d'environnement retirés ; conservées en no-op pour ne pas casser les appels.)
export function isQuoteHistoOn() { return true; }
export function isQuoteLot2On() { return true; }
export function isQuoteLot6On() { return true; }
export function isQuoteOtpOn() { return true; }

// Jeton OTP (numéro vérifié) pour consulter "Mes expéditions" par numéro. sessionStorage :
// débloqué le temps de la session, un nouvel accès redemande un code.
const OTP_TOKEN = 'ogg_otp_token';
export function saveOtpToken(phone, token) {
  if (!phone || !token) return;
  try {
    sessionStorage.setItem(OTP_TOKEN, JSON.stringify({ phone, token }));
  } catch (_) {}
}
export function getOtpToken() {
  try {
    return JSON.parse(sessionStorage.getItem(OTP_TOKEN) || 'null');
  } catch (_) {
    return null;
  }
}
export function clearOtpToken() {
  try {
    sessionStorage.removeItem(OTP_TOKEN);
  } catch (_) {}
}

function readFromUrl() {
  const sp = new URLSearchParams(window.location.search);
  const out = {};
  KEYS.forEach((k) => {
    const v = sp.get(k);
    if (v) out[k] = v;
  });
  return out;
}

function setCookie(name, value) {
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};path=/;max-age=${maxAge};SameSite=Lax`;
}

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  if (!m) return null;
  try {
    return JSON.parse(decodeURIComponent(m[1]));
  } catch (_) {
    return null;
  }
}

// À appeler une fois au démarrage de l'app (App.js).
// First-touch = premier contact, jamais écrasé. Last-touch = dernier contact, toujours mis à jour.
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  const current = readFromUrl();
  if (Object.keys(current).length === 0) return; // aucune info de campagne dans l'URL

  const stamped = {
    ...current,
    landing_page: window.location.pathname,
    referrer: document.referrer || '',
  };

  if (!getCookie(FIRST_TOUCH)) setCookie(FIRST_TOUCH, stamped); // premier contact : ne jamais écraser
  setCookie(LAST_TOUCH, stamped); // dernier contact : toujours à jour
}

// Identifiant de session de devis : relie une estimation à la commande finale (et déduplique
// les leads). Généré une fois, persisté en cookie, réutilisé jusqu'à expiration.
function generateId() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'q_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getQuoteSessionId() {
  if (typeof window === 'undefined') return '';

  // Comportement legacy (drapeau off) : cookie 90 j, réutilisé -> upsert sur la même ligne.
  if (!isQuoteHistoOn()) {
    let sid = getCookie(QUOTE_SID);
    if (sid && typeof sid === 'string') return sid;
    sid = generateId();
    setCookie(QUOTE_SID, sid);
    return sid;
  }

  // Lot 1 : une demande = un session_id, porté par sessionStorage (nouvelle visite = nouvelle ligne).
  try {
    let sid = sessionStorage.getItem(SESSION_SID);
    if (sid) return sid;
    sid = generateId();
    sessionStorage.setItem(SESSION_SID, sid);
    return sid;
  } catch (_) {
    // Navigation privée / stockage indisponible : identifiant éphémère, ne casse pas le parcours.
    return generateId();
  }
}

// Identité stable du navigateur (localStorage). Sert de clé de récurrence. Au premier passage du
// nouveau code, on réutilise l'ancien cookie de session comme device_id pour rattacher les revenants
// à leur historique (le backfill BD fait device_id = session_id sur les lignes existantes).
export function getDeviceId() {
  if (typeof window === 'undefined') return '';
  try {
    let id = localStorage.getItem(DEVICE_ID);
    if (id) return id;
    const legacy = getCookie(QUOTE_SID);
    id = (legacy && typeof legacy === 'string') ? legacy : generateId();
    localStorage.setItem(DEVICE_ID, id);
    return id;
  } catch (_) {
    return generateId();
  }
}

// À appeler après une commande aboutie : la ligne devient convertie et ne doit plus jamais être
// mise à jour par une nouvelle estimation. Le prochain devis obtiendra un session_id neuf (nouvelle ligne).
export function resetQuoteSession() {
  if (typeof window === 'undefined' || !isQuoteHistoOn()) return;
  try {
    sessionStorage.removeItem(SESSION_SID);
  } catch (_) {
    // rien à faire
  }
}

// Préremplissage : dernières valeurs connues du même appareil (nom, téléphone, enlèvement habituel).
// 100 % côté client, aucun appel réseau (perf mobile, pas de fuite de données).
export function getQuotePrefill() {
  if (typeof window === 'undefined' || !isQuoteHistoOn()) return null;
  try {
    return JSON.parse(localStorage.getItem(PREFILL) || 'null');
  } catch (_) {
    return null;
  }
}

export function saveQuotePrefill(values) {
  if (typeof window === 'undefined' || !isQuoteHistoOn() || !values) return;
  try {
    const current = getQuotePrefill() || {};
    localStorage.setItem(PREFILL, JSON.stringify({ ...current, ...values }));
  } catch (_) {
    // stockage indisponible : le préremplissage est un confort, on l'ignore silencieusement
  }
}

// Renvoie l'attribution à joindre à une commande / un événement.
// Priorité au last-touch, repli sur le first-touch.
export function getAttribution() {
  const first = getCookie(FIRST_TOUCH) || {};
  const last = getCookie(LAST_TOUCH) || {};
  const pick = (k) => last[k] || first[k] || '';
  return {
    utm_source: pick('utm_source'),
    utm_medium: pick('utm_medium'),
    utm_campaign: pick('utm_campaign'),
    utm_content: pick('utm_content'),
    utm_term: pick('utm_term'),
    fbclid: pick('fbclid'),
    gclid: pick('gclid'),
    landing_page: last.landing_page || first.landing_page || '',
    referrer: last.referrer || first.referrer || '',
    first_touch: first,
    last_touch: last,
  };
}
